import { useEffect, useRef, useState } from 'react';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInYears,
  isAfter,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const SECONDS_PER_MINUTE = 60;
export const MINUTES_PER_HOUR = 60;
export const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;
export const HOURS_PER_DAY = 24;
export const SECONDS_PER_DAY = SECONDS_PER_HOUR * HOURS_PER_DAY;
export const MILLISECONDS_PER_SECOND = 1000;

export const getISODateUTCArray = (start: Date, end: Date): string[] => {
  return dateRangeArray(start, end).map(formatDateUtcYYYYMMDD);
};

/**
 * Given date strings in YYYY-MM-DD, returns iso dates in UTC, ex. ['2025-01-01', '2025-01-02', ...]
 */
export const getISODateUTCArrayFromDateStrings = (
  start: string,
  end: string,
): string[] => {
  // TODO: validate start and end
  return getISODateUTCArray(new Date(start), new Date(end));
};

/**
 * Adds number of days (24 hours each) to a date.
 *
 * We add 24 hours instead of incrementing by 1 day to avoid issues with daylight savings.
 */
export const addDays = (d: Date, days: number): Date => {
  return new Date(d.getTime() + MILLISECONDS_PER_SECOND * SECONDS_PER_DAY * days);
};

export const dateRangeArray = (start: Date, end: Date): Date[] => {
  const results = [];
  for (let d = start; d <= end; d = addDays(d, 1)) {
    const utcDateString = formatDateGenericObjYYYYMMDD({
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
    });
    results.push(new Date(utcDateString));
  }
  return results;
};

/**
 * Gets the last completed UTC date, which is the current UTC date minus one day
 */
export function getLastCompletedUtcDate(local_date: Date) {
  const utcDate = new Date(
    Date.UTC(
      local_date.getUTCFullYear(),
      local_date.getUTCMonth(),
      local_date.getUTCDate() - 1,
      0,
      0,
      0,
      0,
    ),
  );
  return formatDateUtcYYYYMMDD(utcDate);
}

/**
 * Format date generic obj
 */
export function formatDateGenericObjYYYYMMDD(date: {
  year: number;
  month: number;
  day: number;
}) {
  const year = date.year;
  const month = date.month.toString().padStart(2, '0');
  const day = date.day.toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Format date into UTC 'YYYY-MM-DD'
 */
export function formatDateUtcYYYYMMDD(date: Date) {
  return formatDateGenericObjYYYYMMDD({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
}

/**
 * When possible, use formatDateCommon instead
 */
export const formatLocalDate = (date: Date, fmt: string) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatInTimeZone(date, timeZone, fmt);
};

/**
 * Relative and absolute local dates should be prefered,
 * but UTC dates should be available on hover.
 * When possible, use formatDateComon instead
 */
export const formatUtcDate = (date: Date): string => {
  return date.toUTCString();
};

/**
 * Absolute dates should be used when an event is > 6 hours in the past or future
 * Absolute dates are timezone localized
 * When possible, use formatDateCommon instead
 */
export const formatAbsoluteLocalDate = (date: Date): string => {
  return formatLocalDate(date, 'PP, p z');
};

/**
 * Relative dates should be used when an event is < 6 hours in the past or future
 * When possible, use formatDateCommon instead
 */
export const formatRelativeDate = (date: Date): string => {
  const now = new Date();

  const isInFuture = isAfter(date, now);

  const diffInHours = Math.abs(differenceInHours(now, date));
  const diffInSeconds = Math.abs(differenceInSeconds(now, date));
  const diffInMinutes = Math.abs(differenceInMinutes(now, date));

  let relativeTime = '';
  if (diffInHours < 1) {
    relativeTime = `${diffInMinutes}m ${diffInSeconds - 60 * diffInMinutes}s`;
  } else {
    relativeTime = `${diffInHours}h ${diffInMinutes - 60 * diffInHours}m`;
  }
  return isInFuture ? `in ${relativeTime}` : `${relativeTime} ago`;
};

/*
 * In general throughout our app:
 * - If the event is < 6 hours away, in the past or future:
 *   - Abbreviated relative time should be used
 *   - If < 1 hour away: m, s ago e.g. 7m 35s ago
 *   - If > 1 hour away: h, m ago e.g. in 1h 23m
 * - If the event is >= 6 hours away, in the past or future:
 *   - Absolute local time should be used
 *   - month, day, year, hour:minute timezone e.g. Aug 3, 2022, 2:20 PM EST
 * - UTC timestamp should be available via tooltip on hover
 */
export interface CommonDateFormat {
  humanized?: string;
  utc?: string;
}

export const formatDateCommon = (date: Date): CommonDateFormat => {
  const now = new Date();
  const diffInHours = Math.abs(differenceInHours(now, date));

  let humanized = '';
  const utc = formatUtcDate(date);

  if (diffInHours < 6) {
    humanized = formatRelativeDate(date);
  } else {
    humanized = formatAbsoluteLocalDate(date);
  }

  return {
    humanized,
    utc,
  };
};

// The backend returns humanized durations using different formatting.
// We do some find and replaces here to match the formatting we use
// for dates elsewhere in the frontend
export const formatHumanizedDuration = (duration: string = ''): string => {
  return duration
    .toLowerCase()
    .replace(/ hours?/, 'h')
    .replace(/ minutes?/, 'm')
    .replace(/ seconds?/, 's');
};

/**
 * The backend returns non-humanized duration in a hh:mm:ss format, we should humanize it ourselves.
 * Best to not rely on the BE for humanization, sometimes they return 0 minutes when we want 0 seconds
 */
export const humanizeDuration = (duration: string = '00:00:00'): string => {
  const timingParts = duration.split(':');
  const hours = Number(timingParts.length > 0 ? timingParts[0] : 0);
  const minutes = Number(timingParts.length > 1 ? timingParts[1] : 0);
  const seconds = Number(timingParts.length > 2 ? timingParts[2] : 0);

  let timing = `${seconds}s`;

  if (minutes > 0) {
    timing = `${minutes}m ${seconds}s`;
  }

  if (hours > 0) {
    timing = `${hours}h ${minutes}m ${seconds}s`;
  }

  return timing;
};

export const humanizeSeconds = (seconds: number): string => {
  const hours = seconds / 3600;
  const mins = (seconds % 3600) / 60;
  const secs = seconds % 60;
  return humanizeDuration(`${hours}:${mins}:${secs}`);
};

export const parseDateAsLocal = (dateString: string) => {
  // when we say new Date("2023-06-01") that'll be interpreted as UTC, but
  // we want it to be treated as the local timezone.
  const date = new Date(dateString);

  // split "2023-06-01T00:00:00.000Z" into a list of its numeric parts.
  const parts = date.toISOString().split(/\D/);

  // convert the parts to actual numbers.
  const numbers = parts.slice(0, 6).map((i) => parseInt(i));

  // build a new date using the year, month, day, h/m/s numbers.
  // we subtract 1 from the month because it's 0-based. the ISO string
  // will say "1" for january but we want to pass in zero here.
  return new Date(
    numbers[0],
    numbers[1] - 1,
    numbers[2],
    numbers[3],
    numbers[4],
    numbers[5],
  );
};

const secondsInHour = 3600;
const secondsInMinute = 60;

export const formatDistanceShort = (
  seconds: number,
  full: boolean = false,
  includeSeconds: boolean = true,
): string => {
  let hasHour: boolean = false;
  const duration: string[] = [];
  if (seconds >= 0) {
    if (seconds >= secondsInHour) {
      duration.push(`${String(Math.trunc(seconds / secondsInHour))}h`);
      seconds %= secondsInHour;
      hasHour = true;
    }
    if (seconds >= secondsInMinute) {
      duration.push(`${String(Math.trunc(seconds / secondsInMinute))}m`);
      seconds %= secondsInMinute;
    }
    // Do not show seconds if hour is shown so we can keep it short
    if (includeSeconds) {
      if (!hasHour || full) {
        if (seconds >= 1 && !full) {
          duration.push(`${String(Math.trunc(seconds))}s`);
        } else if (seconds > 0) {
          duration.push(`${seconds.toFixed(2)}s`);
        }
      }
    }
  } else {
    return String(seconds);
  }

  return duration.join(', ');
};

/**
 * Get the current time immediately, and update the time
 * when the clock changes to a new minute.
 */
export const useCurrentTime = (
  /** Used for mocking in tests */
  now?: () => Date,
) => {
  const getTime = () => now?.() ?? new Date();
  const [nowState, setNowState] = useState(getTime());

  const getTimeRef = useRef(getTime);
  getTimeRef.current = getTime;

  useEffect(() => {
    let currentTime = getTimeRef.current();
    const intervalMillis = 60 * 1000; // 60 seconds
    const delay = intervalMillis - (currentTime.getTime() % intervalMillis);

    let interval: ReturnType<typeof setInterval>;

    const timeout = setTimeout(() => {
      currentTime = getTimeRef.current();
      setNowState(currentTime);
      interval = setInterval(() => {
        currentTime = getTimeRef.current();
        setNowState(currentTime);
      }, intervalMillis);
    }, delay);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  return nowState;
};

export const daysFromToday = (date: Date | string): number => {
  if (typeof date === 'string') {
    date = new Date(date);
  }

  const now = new Date();
  const nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const dateUtc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  );

  const millisecondsInDay = 1000 * 60 * 60 * 24;
  return Math.floor((nowUtc - dateUtc) / millisecondsInDay);
};

export const formatDateWithTooltip = (
  date: Date,
): { display: string; tooltip: string } => {
  const formatted = formatDateCommon(date);
  return {
    display: formatAbsoluteLocalDate(date),
    tooltip: formatted.utc || '',
  };
};

export const toRelativeDate = (date: Date): string => {
  const now = new Date();
  const diffInHours = Math.abs(differenceInHours(now, date));
  const diffInMinutes = Math.abs(differenceInMinutes(now, date));
  const diffInDays = Math.abs(differenceInDays(now, date));
  const diffInMonths = Math.abs(differenceInMonths(now, date));
  const diffInYears = Math.abs(differenceInYears(now, date));

  if (diffInYears > 0) {
    return `${diffInYears}y ago`;
  } else if (diffInMonths > 0) {
    return `${diffInMonths}mo ago`;
  } else if (diffInDays > 0) {
    return `${diffInDays}d ago`;
  } else if (diffInHours > 0) {
    return `${diffInHours}h ago`;
  }
  return `${diffInMinutes}m ago`;
};
